import { useEffect, useState } from "react";
import { client } from "../net/client";
import { useSignal } from "../net/signal";
import { fa } from "../game/cards";
import type { MatchStatus } from "../gen/bazivan/v1/table_pb";
import { arenaStatus, cupLine, cupNote, cupRef, isArena, isInvitational, listWhen, phaseAt, type Campaign, type Detail } from "./cup";
import { playAfterAd } from "./Ads";

/**
 * تکه‌های جامِ آرنا («جامِ شبِ جمعه») بیرون از صفحه‌ی خودِ جام — docs/friday-cup.md:
 *
 *   • صفِ جستجو که بگوید «صفِ جام» است و نه صفِ عادی،
 *   • نتیجه‌ی هر میزِ جام روی پرده‌ی پایانِ مسابقه، با «بازیِ بعدی در جام»،
 *   • نوارِ جام در لابی، با شمارشِ معکوس تا شروع.
 */

/** ساعتی که هر چند ثانیه جلو می‌رود — برای شمارشِ معکوس و عوض‌شدنِ مرحله سرِ ۲۱:۰۰. */
export function useNow(every = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), every);
    return () => clearInterval(t);
  }, [every]);
  return now;
}

/**
 * «در جست‌وجوی هم‌بازی…» — و اگر صفِ جام است، همین را بگوید.
 *
 * صفِ جام ۳۰ ثانیه برای آدم صبر می‌کند و نه ۸؛ بی این برچسب، بازیکن فکر
 * می‌کرد صفِ عادی گیر کرده و «بی‌خیال» می‌زد.
 */
export function MatchSearching({ match, className = "stack" }: { match: MatchStatus; className?: string }) {
  const cup = useSignal(client.cup);
  const cupTitle = match.cup ? (cup?.id === match.cup && cup.title) || "جام" : "";
  return (
    <div className={className}>
      <div className="searching">
        <i className="dot busy" aria-hidden="true" />
        {cupTitle ? `صف ${cupTitle}…` : "در جست‌وجوی هم‌بازی…"} {fa(match.seated)} از {fa(match.seats)}
      </div>
      {match.botFillInSec > 0 && (
        <div className="muted">تا {fa(match.botFillInSec)} ثانیه‌ی دیگر جای خالی با ربات پر می‌شود.</div>
      )}
      {cupTitle && <div className="muted">صف جام دیرتر ربات می‌گیرد تا میز با آدم پر شود: برد میز بی‌ربات امتیاز کامل دارد.</div>}
      <button className="btn ghost" onClick={() => client.cancelMatch()}>بی‌خیال</button>
    </div>
  );
}

/** «بازیِ بعدی در جام» در دسترس است؟ آن‌وقت دکمه‌ی برگشت کم‌رنگ می‌شود — یک کارِ اصلی. */
export function useCupNext(): boolean {
  const cup = useSignal(client.cup);
  return !!cup && cup.endsAtMs > Date.now();
}

/**
 * نتیجه‌ی میزِ جام روی پرده‌ی پایانِ مسابقه — «+۲ امتیازِ جام · رتبه‌ی ۳ از ۲۵ 🔥» —
 * و تا جام باز است، «بازیِ بعدی در جام» که به صفِ جام برمی‌گردد و نه به صفِ عادی.
 *
 * نتیجه کمی بعد از خودِ پرده می‌رسد (سرور اول پاداشِ میز را می‌دهد، بعد جدول
 * را)، پس تا رسیدنش جای خالی است و نه عددِ حدسی.
 */
export function CupAfter() {
  const u = useSignal(client.cupUpdate);
  const cup = useSignal(client.cup);
  const live = cup && cup.endsAtMs > Date.now() ? cup : null;

  // صفحه وسطِ میزِ جام تازه شد: نتیجه رسید ولی نمی‌دانیم کدام جام بود. یک بار
  // می‌پرسیم تا «بازیِ بعدی» هم بداند کجا برگردد.
  useEffect(() => {
    if (!u || client.cup.value) return;
    let off = false;
    client.rest<Detail>("GET", "/v1/campaigns/" + encodeURIComponent(u.cupId)).then((d) => {
      if (!off && !client.cup.value && phaseAt(d.campaign) === "live") client.cup.value = cupRef(d.campaign);
    }, () => { /* بی «بازیِ بعدی» هم نتیجه سرِ جایش است */ });
    return () => { off = true; };
  }, [u]);

  if (!u && !live) return null;
  return (
    <div className="stack cupafter">
      {u && (
        <div className={"cuppill" + (u.doubled ? " hot" : "")} role="status">
          <b>{cupLine(u)}</b>
          <span>{cupNote(u)}</span>
        </div>
      )}
      {live && (
        <button className="btn" onClick={() => { client.leaveRoom(); playAfterAd(() => client.cupMatch(live), "cup"); }}>
          بازی بعدی در جام
        </button>
      )}
    </div>
  );
}

/**
 * نوارِ جام در لابی: «جامِ شبِ جمعه · ۲ روز و ۳ ساعت تا شروع».
 *
 * «لیگ و جام» عمداً از لابی رفته بود (جایش لندینگ است)، ولی جامِ آرنا کارش
 * همین است که بازیکن‌های پراکنده را سرِ یک ساعت جمع کند — کسی که از شروعش
 * خبر ندارد نمی‌آید. پس فقط همین یکی، و فقط وقتی واقعاً جامی در راه یا در
 * جریان است؛ جامِ تمام‌شده جای لابی را نمی‌گیرد.
 */
export function CupStrip({ onOpen }: { onOpen: (id: string) => void }) {
  const [c, setC] = useState<Campaign | null>(null);
  const now = useNow();
  useEffect(() => {
    let off = false;
    client.rest<{ campaigns: Campaign[] }>("GET", "/v1/campaigns?upcoming=1&party=1&gems=1").then((r) => {
      if (off) return;
      // رقابتِ دعوتی ثبت‌نام ندارد؛ نوارِ لابی برای جمع‌کردنِ بازیکن است.
      setC((r.campaigns ?? []).find((x) => isArena(x) && !isInvitational(x) && open(phaseAt(x))) ?? null);
    }, () => { /* بی سرور، بی نوار — لابی کارش را می‌کند */ });
    return () => { off = true; };
  }, []);
  if (!c) return null;
  const ph = phaseAt(c, now);
  if (!open(ph)) return null;
  const line = ph === "upcoming" ? arenaStatus(c, now) : listWhen(c, now);
  return (
    <button className={"cupstrip" + (ph === "live" ? " live" : "")} onClick={() => onOpen(c.id)}
            aria-label={`${c.title} — ${line}`}>
      <span className="ic" aria-hidden="true">🏅</span>
      <span className="txt">
        <b>{c.title}</b>
        <span>{line}</span>
      </span>
      <span className="go" aria-hidden="true">←</span>
    </button>
  );
}

const open = (ph: string) => ph === "upcoming" || ph === "live" || ph === "closing";
